import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, FileSignature, Plus, Trash2, GripVertical, Save,
  RotateCcw, Loader2, ChevronUp, ChevronDown, Info
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import api from '../lib/api';
import { toast } from 'sonner';

const PLACEHOLDERS_LEASE = [
  { key: '{bien_titre}', desc: 'Titre du bien' },
  { key: '{bien_adresse}', desc: 'Adresse du bien' },
  { key: '{bien_type}', desc: 'Type de bien' },
  { key: '{bien_reference}', desc: 'Reference du bien' },
  { key: '{loyer}', desc: 'Montant du loyer' },
  { key: '{date_effet}', desc: 'Date d\'effet du bail' },
  { key: '{locataire_nom}', desc: 'Nom du locataire' },
  { key: '{proprietaire_nom}', desc: 'Nom du proprietaire' },
];

const PLACEHOLDERS_MANDATE = [
  { key: '{type_transaction}', desc: 'Type (vente/location)' },
  { key: '{bien_titre}', desc: 'Titre du bien' },
  { key: '{bien_adresse}', desc: 'Adresse du bien' },
  { key: '{prix}', desc: 'Prix du bien' },
  { key: '{commission_rate}', desc: 'Taux de commission' },
  { key: '{proprietaire_nom}', desc: 'Nom du proprietaire' },
  { key: '{agence_nom}', desc: 'Nom de l\'agence' },
];

const PLACEHOLDERS_EMPLOYMENT = [
  { key: '{{user_name}}', desc: 'Nom du collaborateur' },
  { key: '{{employee_id}}', desc: 'Matricule' },
  { key: '{{email}}', desc: 'Email' },
  { key: '{{phone}}', desc: 'Telephone' },
  { key: '{{address}}', desc: 'Adresse' },
  { key: '{{birth_date}}', desc: 'Date de naissance' },
  { key: '{{birth_place}}', desc: 'Lieu de naissance' },
  { key: '{{nationality}}', desc: 'Nationalite' },
  { key: '{{position}}', desc: 'Fonction / Poste' },
  { key: '{{department}}', desc: 'Departement' },
  { key: '{{hire_date}}', desc: 'Date d\'embauche' },
  { key: '{{contract_type}}', desc: 'Type (CDI/CDD/STAGE)' },
  { key: '{{contract_start_date}}', desc: 'Date de debut du contrat' },
  { key: '{{contract_end_date}}', desc: 'Date de fin du contrat' },
  { key: '{{monthly_salary}}', desc: 'Salaire mensuel brut' },
  { key: '{{agence_nom}}', desc: 'Nom de l\'agence' },
  { key: '{{today}}', desc: 'Date du jour' },
];

const DocumentTemplates = () => {
  const [templates, setTemplates] = useState({
    lease_contract: { articles: [], isDefault: true },
    brokerage_mandate: { articles: [], isDefault: true },
    employment_contract: { articles: [], isDefault: true },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(null);
  const [activeTab, setActiveTab] = useState('lease_contract');

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const [leaseRes, mandateRes, employmentRes] = await Promise.all([
        api.get('/document-templates/lease_contract'),
        api.get('/document-templates/brokerage_mandate'),
        api.get('/document-templates/employment_contract'),
      ]);
      setTemplates({
        lease_contract: { articles: leaseRes.data.articles || [], isDefault: leaseRes.data.is_default !== false },
        brokerage_mandate: { articles: mandateRes.data.articles || [], isDefault: mandateRes.data.is_default !== false },
        employment_contract: { articles: employmentRes.data.articles || [], isDefault: employmentRes.data.is_default !== false },
      });
    } catch {
      toast.error('Erreur de chargement des templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const setArticlesFor = (docType, updater) => {
    setTemplates(prev => {
      const current = prev[docType].articles;
      const next = typeof updater === 'function' ? updater(current) : updater;
      return { ...prev, [docType]: { ...prev[docType], articles: next } };
    });
  };

  const handleSave = async (docType) => {
    setSaving(true);
    try {
      await api.put(`/document-templates/${docType}`, { articles: templates[docType].articles });
      toast.success('Template sauvegarde');
      setTemplates(prev => ({ ...prev, [docType]: { ...prev[docType], isDefault: false } }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (docType) => {
    try {
      const res = await api.post(`/document-templates/${docType}/reset`);
      setTemplates(prev => ({ ...prev, [docType]: { articles: res.data.articles, isDefault: true } }));
      toast.success('Template reinitialise');
    } catch {
      toast.error('Erreur');
    }
    setShowResetConfirm(null);
  };

  const addArticle = (docType) => {
    setArticlesFor(docType, (articles) => {
      const newOrder = articles.length + 1;
      return [...articles, {
        id: `new-${Date.now()}`,
        title: `ARTICLE ${newOrder} - NOUVEAU`,
        content: '',
        order: newOrder,
      }];
    });
  };

  const removeArticle = (docType, idx) => {
    setArticlesFor(docType, (articles) => articles.filter((_, i) => i !== idx).map((a, i) => ({ ...a, order: i + 1 })));
  };

  const moveArticle = (docType, idx, direction) => {
    setArticlesFor(docType, (articles) => {
      const arr = [...articles];
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= arr.length) return arr;
      [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
      return arr.map((a, i) => ({ ...a, order: i + 1 }));
    });
  };

  const updateArticle = (docType, idx, field, value) => {
    setArticlesFor(docType, (articles) => articles.map((a, i) => i === idx ? { ...a, [field]: value } : a));
  };

  const renderArticlesEditor = (docType, articles, placeholders, isDefault) => (
    <div className="space-y-4">
      {/* Placeholders info */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-3">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-blue-800">Variables disponibles</p>
              <p className="text-xs text-blue-600 mt-1">
                Utilisez ces variables dans le contenu, elles seront remplacees automatiquement dans le PDF :
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                {placeholders.map(p => (
                  <Badge key={p.key} variant="outline" className="bg-white text-xs font-mono cursor-help" title={p.desc}>
                    {p.key}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Articles list */}
      {articles.map((article, idx) => (
        <Card key={article.id || idx} data-testid={`article-${docType}-${idx}`}>
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              {/* Order controls */}
              <div className="flex flex-col items-center gap-1 pt-1">
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0"
                  onClick={() => moveArticle(docType, idx, -1)} disabled={idx === 0}>
                  <ChevronUp className="w-4 h-4" />
                </Button>
                <span className="text-xs font-mono text-gray-400">{idx + 1}</span>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0"
                  onClick={() => moveArticle(docType, idx, 1)} disabled={idx === articles.length - 1}>
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </div>
              {/* Content */}
              <div className="flex-1 space-y-3">
                <Input
                  value={article.title}
                  onChange={e => updateArticle(docType, idx, 'title', e.target.value)}
                  className="font-semibold"
                  placeholder="Titre de l'article"
                  data-testid={`article-title-${idx}`}
                />
                <Textarea
                  value={article.content}
                  onChange={e => updateArticle(docType, idx, 'content', e.target.value)}
                  rows={4}
                  placeholder="Contenu de l'article... Utilisez les variables comme {loyer}, {locataire_nom}, etc."
                  className="text-sm"
                  data-testid={`article-content-${idx}`}
                />
              </div>
              {/* Delete */}
              <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50"
                onClick={() => removeArticle(docType, idx)} data-testid={`delete-article-${idx}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Action buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button variant="outline" onClick={() => addArticle(docType)}
          className="border-dashed border-2" data-testid={`add-article-${docType}`}>
          <Plus className="w-4 h-4 mr-2" /> Ajouter un article
        </Button>
        <div className="flex-1" />
        <Button variant="outline" onClick={() => setShowResetConfirm(docType)}
          className="text-orange-600 border-orange-300 hover:bg-orange-50">
          <RotateCcw className="w-4 h-4 mr-2" /> Reinitialiser
        </Button>
        <Button onClick={() => handleSave(docType)} disabled={saving}
          className="bg-[#1B3A4B] hover:bg-[#142D3A]" data-testid={`save-template-${docType}`}>
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Sauvegarder
        </Button>
      </div>

      {isDefault && (
        <p className="text-xs text-gray-400 text-center">Template par defaut. Modifiez et sauvegardez pour personnaliser.</p>
      )}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="document-templates-page">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">
          Modeles de Documents
        </h1>
        <p className="text-gray-500 mt-1">
          Personnalisez les articles des contrats de location, mandats de courtage et contrats de travail
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#1B3A4B]" /></div>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-3 w-full max-w-2xl">
            <TabsTrigger value="lease_contract" data-testid="tab-lease">
              <FileText className="w-4 h-4 mr-2" /> Contrat de Location
            </TabsTrigger>
            <TabsTrigger value="brokerage_mandate" data-testid="tab-mandate">
              <FileSignature className="w-4 h-4 mr-2" /> Mandat de Courtage
            </TabsTrigger>
            <TabsTrigger value="employment_contract" data-testid="tab-employment">
              <FileText className="w-4 h-4 mr-2" /> Contrat de Travail
            </TabsTrigger>
          </TabsList>

          <TabsContent value="lease_contract" className="mt-4">
            {renderArticlesEditor('lease_contract', templates.lease_contract.articles, PLACEHOLDERS_LEASE, templates.lease_contract.isDefault)}
          </TabsContent>

          <TabsContent value="brokerage_mandate" className="mt-4">
            {renderArticlesEditor('brokerage_mandate', templates.brokerage_mandate.articles, PLACEHOLDERS_MANDATE, templates.brokerage_mandate.isDefault)}
          </TabsContent>

          <TabsContent value="employment_contract" className="mt-4">
            {renderArticlesEditor('employment_contract', templates.employment_contract.articles, PLACEHOLDERS_EMPLOYMENT, templates.employment_contract.isDefault)}
          </TabsContent>
        </Tabs>
      )}

      {/* Reset confirmation dialog */}
      <Dialog open={!!showResetConfirm} onOpenChange={() => setShowResetConfirm(null)}>
        <DialogContent className="sm:max-w-[380px]">
          <DialogHeader>
            <DialogTitle>Reinitialiser le template ?</DialogTitle>
            <DialogDescription>
              Tous vos articles personnalises seront remplaces par les articles par defaut. Cette action est irreversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetConfirm(null)}>Annuler</Button>
            <Button variant="destructive" onClick={() => handleReset(showResetConfirm)} data-testid="confirm-reset">
              Reinitialiser
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentTemplates;
